import Vue from 'vue';
import { HTTP_BAD_REQUEST } from '@/utils/http';
import {
  API_ENDPOINTS,
  EMAIL_NOTIFICATION,
  EMAIL_ACTIVITY_LOG_LEVEL,
  NOTIFICATION_FEATURE_ICON,
  NOTIFICATION_FEATURE_KIND,
  NOTIFICATION_FEATURE_LABEL,
  NOTIFICATION_KIND,
  NOTIFICATION_SCHEDULE,
  NOTIFICATION_TYPE_ALREADY_CONFIGURED,
  NOTIFICATION_TYPE_KEYS,
  SETUP_FEATURE_TYPES,
} from '../utils/constants';
import { getApiErrorMessage } from '../utils/apiErrorUtils';
import {
  isNotificationEnabled,
  normalizeNotificationList,
  normalizeNotificationRecord,
  resolveNotificationFeature,
} from '../utils/notificationRecordUtils';
import { buildScheduleCron, buildSchedulePayload } from '../utils/notificationScheduleUtils';

function getAxios() {
  return Vue.axios;
}

const DEFAULT_CONFIG = {
  ui: { email_activity_log: EMAIL_ACTIVITY_LOG_LEVEL.DETAILED },
  defaults: {},
  sla: [],
  priorities: [],
  limits: {},
};

function kindOf(doc) {
  return doc?.kind || NOTIFICATION_FEATURE_KIND[doc?.feature] || null;
}

export default {
  namespaced: true,

  state: {
    locations: [],
    categories: [],
    isNotificationEnabled: false,
    availableNotifications: [],
    createdNotifications: [],
    notificationConfig: { ...DEFAULT_CONFIG },
    activityLogs: { items: [], total: 0, page: 1, pageSize: 25, level: EMAIL_ACTIVITY_LOG_LEVEL.DETAILED },
    loading: { list: false, save: false, activity: false },
    pendingStatusIds: [],
    pendingDeleteIds: [],
  },

  mutations: {
    SET_CATEGORIES(state, categories) {
      state.categories = categories;
    },
    SET_AVAILABLE_NOTIFICATIONS(state, notifications) {
      state.availableNotifications = notifications;
    },
    SET_NOTIFICATION_ENABLED(state, enabled) {
      state.isNotificationEnabled = enabled;
    },
    SET_LOCATIONS(state, locations) {
      state.locations = locations;
    },
    SET_ALL_CREATED_NOTIFICATIONS(state, notifications) {
      state.createdNotifications = normalizeNotificationList(notifications);
    },
    UPSERT_NOTIFICATION(state, doc) {
      const normalized = normalizeNotificationRecord(doc);
      if (!normalized?._id) return;
      const index = state.createdNotifications.findIndex((item) => String(item._id) === String(normalized._id));
      if (index >= 0) {
        state.createdNotifications.splice(index, 1, {
          ...state.createdNotifications[index],
          ...normalized,
          feature: normalized.feature || state.createdNotifications[index].feature,
        });
      } else {
        state.createdNotifications.push(normalized);
      }
    },
    REMOVE_NOTIFICATION(state, id) {
      state.createdNotifications = state.createdNotifications.filter((item) => String(item._id) !== String(id));
    },
    SET_NOTIFICATION_CONFIG(state, config) {
      state.notificationConfig = {
        ...DEFAULT_CONFIG,
        ...(config || {}),
        ui: { ...DEFAULT_CONFIG.ui, ...(config?.ui || {}) },
      };
    },
    SET_ACTIVITY_LOGS(state, { items, total, page, pageSize, level }) {
      state.activityLogs = {
        items: items || [],
        total: total || 0,
        page: page || 1,
        pageSize: pageSize || 25,
        level: level || state.activityLogs.level,
      };
    },
    SET_LOADING(state, { key, value }) {
      state.loading = { ...state.loading, [key]: value };
    },
    SET_STATUS_PENDING(state, { id, pending }) {
      const sid = String(id);
      if (pending) {
        if (!state.pendingStatusIds.includes(sid)) state.pendingStatusIds = [...state.pendingStatusIds, sid];
      } else {
        state.pendingStatusIds = state.pendingStatusIds.filter((item) => item !== sid);
      }
    },
    SET_DELETE_PENDING(state, { id, pending }) {
      const sid = String(id);
      if (pending) {
        if (!state.pendingDeleteIds.includes(sid)) state.pendingDeleteIds = [...state.pendingDeleteIds, sid];
      } else {
        state.pendingDeleteIds = state.pendingDeleteIds.filter((item) => item !== sid);
      }
    },
  },

  actions: {
    async fetchAvailableNotifications({ commit }) {
      try {
        const response = await getAxios().post(API_ENDPOINTS.GET_NOTIFICATION_TYPES, { type: EMAIL_NOTIFICATION.TYPE });
        commit('SET_AVAILABLE_NOTIFICATIONS', Array.isArray(response?.data?.data) ? response.data.data : []);
      } catch (error) {
        console.error('[emailNotifications]: fetchAvailableNotifications', error);
        commit('SET_AVAILABLE_NOTIFICATIONS', []);
        throw error;
      }
    },
    async fetchLocations({ commit }) {
      try {
        const locationResponse = await getAxios().post(API_ENDPOINTS.FC_LOCATION_GET);
        if (locationResponse?.data?.status === 'success' && locationResponse?.data?.data) {
          commit('SET_LOCATIONS', locationResponse.data.data);
        } else {
          commit('SET_LOCATIONS', []);
        }
      } catch (error) {
        commit('SET_LOCATIONS', []);
        throw new Error(getApiErrorMessage(error, 'Failed to fetch locations'));
      }
    },
    async fetchNotificationEnabled({ commit, rootState }) {
      try {
        const clientId = rootState.client;
        const response = await getAxios().post(`${API_ENDPOINTS.GET_CLIENT}${clientId}`);
        if (response?.data?.status === 'success' && response.data.data?.length) {
          commit('SET_NOTIFICATION_ENABLED', !!response.data.data[0]?.schedulerEnabled);
        } else {
          commit('SET_NOTIFICATION_ENABLED', false);
        }
      } catch (error) {
        commit('SET_NOTIFICATION_ENABLED', false);
      }
    },
    async fetchAllCreatedNotifications({ commit }) {
      commit('SET_LOADING', { key: 'list', value: true });
      try {
        const response = await getAxios().post(API_ENDPOINTS.GET_NOTIFICATIONS_BY_TYPE, { type: EMAIL_NOTIFICATION.TYPE });
        if (response?.data && Array.isArray(response.data.data)) {
          commit('SET_ALL_CREATED_NOTIFICATIONS', response.data.data);
        }
      } catch (error) {
        throw new Error(getApiErrorMessage(error, 'Failed to fetch notifications'));
      } finally {
        commit('SET_LOADING', { key: 'list', value: false });
      }
    },
    async fetchNotificationConfig({ commit }) {
      try {
        const response = await getAxios().post(API_ENDPOINTS.GET_NOTIFICATION_CONFIG, {});
        if (response?.data?.status === 'success') {
          commit('SET_NOTIFICATION_CONFIG', response.data.data || {});
        }
      } catch (error) {
        console.error('[emailNotifications]: fetchNotificationConfig', error);
      }
    },
    async fetchNotificationActivityLogs({ commit, getters }, { page = 1, pageSize = 25, filters = {} } = {}) {
      if (!getters.isEmailActivityVisible) {
        commit('SET_ACTIVITY_LOGS', { items: [], total: 0, page, pageSize, level: EMAIL_ACTIVITY_LOG_LEVEL.NONE });
        return;
      }
      commit('SET_LOADING', { key: 'activity', value: true });
      try {
        const response = await getAxios().post(API_ENDPOINTS.GET_NOTIFICATION_ACTIVITY_LOGS, {
          page,
          pageSize,
          filters,
        });
        const data = response?.data?.data || {};
        commit('SET_ACTIVITY_LOGS', {
          items: data.items || [],
          total: data.total || 0,
          page: data.page || page,
          pageSize: data.pageSize || pageSize,
          level: data.level || getters.emailActivityLogLevel,
        });
      } catch (error) {
        console.error('[emailNotifications]: fetchNotificationActivityLogs', error);
        commit('sbError', getApiErrorMessage(error, EMAIL_NOTIFICATION.MESSAGES.FAILED_GET), { root: true });
      } finally {
        commit('SET_LOADING', { key: 'activity', value: false });
      }
    },
    async fetchCategories({ commit }) {
      try {
        const response = await getAxios().post(API_ENDPOINTS.CHART_OF_ACCOUNT_GET);
        if (response?.data?.status === 'success') {
          const categorySet = new Set();
          (response.data.data || []).forEach((item) => {
            if (item?.category && typeof item.category === 'string') categorySet.add(item.category);
          });
          commit('SET_CATEGORIES', Array.from(categorySet).map((category) => ({ id: category, name: category })));
        } else {
          commit('SET_CATEGORIES', []);
        }
      } catch (error) {
        console.error('[emailNotifications]: fetchCategories', error);
        commit('SET_CATEGORIES', []);
      }
    },
    async saveNotification({ commit }, { type, payload }) {
      commit('SET_LOADING', { key: 'save', value: true });
      try {
        const isSetup = SETUP_FEATURE_TYPES.has(type);
        const endpoint = isSetup
          ? API_ENDPOINTS.CREATE_OR_UPDATE_TASK_DUE_NOTIFICATION
          : API_ENDPOINTS.ADD_AND_UPDATE_ESCALATION_AND_DASHBOARD_NOTIFICATION;
        if (!endpoint) throw new Error('Invalid notification type');

        const featureDetails = { ...(payload?.featureDetails || payload || {}), feature: type };
        const kind = NOTIFICATION_FEATURE_KIND[type];
        if (kind === NOTIFICATION_KIND.SCHEDULE || kind === NOTIFICATION_KIND.TEMPLATE) {
          const startTime = featureDetails.meta?.schedule?.startTime
            || featureDetails.startTime
            || NOTIFICATION_SCHEDULE.DEFAULT_START_TIME;
          const schedule = {
            ...(featureDetails.meta?.schedule || {}),
            ...buildSchedulePayload({ startTime }),
            frequencyHours: featureDetails.meta?.schedule?.frequencyHours ?? 24,
          };
          featureDetails.meta = {
            ...(featureDetails.meta || {}),
            schedule,
          };
          if (kind === NOTIFICATION_KIND.TEMPLATE) {
            if (!featureDetails.type) {
              featureDetails.type = EMAIL_NOTIFICATION.TYPE;
            }
            const { cron } = buildScheduleCron(schedule);
            featureDetails.schedulerTiming = cron;
          } else {
            delete featureDetails.schedulerTiming;
          }
        }
        const response = await getAxios().post(endpoint, { featureDetails });
        const doc = response?.data?.data?.[0] || response?.data?.data;
        if (doc) {
          commit('UPSERT_NOTIFICATION', normalizeNotificationRecord({
            ...featureDetails,
            ...doc,
            feature: doc.feature || type,
          }));
        }
        commit('sbSuccess', response?.data?.message || EMAIL_NOTIFICATION.MESSAGES.SUCCESS_CREATE, { root: true });
        return response?.data;
      } catch (error) {
        console.error('[emailNotifications]: saveNotification', error);
        if (error?.response?.status !== HTTP_BAD_REQUEST) {
          commit(
            'sbError',
            getApiErrorMessage(error, EMAIL_NOTIFICATION.MESSAGES.FAILED_UPDATE),
            { root: true }
          );
        }
        throw error;
      } finally {
        commit('SET_LOADING', { key: 'save', value: false });
      }
    },
    async changeStatusForNotification({ commit }, { id, status }) {
      commit('SET_STATUS_PENDING', { id, pending: true });
      const enabled = status === EMAIL_NOTIFICATION.STATUS.ENABLED;
      try {
        const response = await getAxios().post(`${API_ENDPOINTS.CHANGE_NOTIFICATION_STATUS}/${id}`, { action: status });
        const doc = response?.data?.data?.[0];
        commit('UPSERT_NOTIFICATION', normalizeNotificationRecord({
          ...(doc || {}),
          _id: doc?._id || id,
          status,
          flag: enabled,
        }));
        commit('sbSuccess', EMAIL_NOTIFICATION.MESSAGES.SUCCESS_STATUS_CHANGE, { root: true });
      } catch (error) {
        console.error('[emailNotifications]: changeStatusForNotification', error);
        commit('sbError', getApiErrorMessage(error, EMAIL_NOTIFICATION.MESSAGES.FAILED_UPDATE), { root: true });
        throw error;
      } finally {
        commit('SET_STATUS_PENDING', { id, pending: false });
      }
    },
    async deleteNotification({ commit }, { id }) {
      commit('SET_DELETE_PENDING', { id, pending: true });
      try {
        await getAxios().post(`${API_ENDPOINTS.DELETE_SETUP_NOTIFICATION}/${id}`, {});
        commit('REMOVE_NOTIFICATION', id);
        commit('sbSuccess', EMAIL_NOTIFICATION.MESSAGES.SUCCESS_DELETE, { root: true });
      } catch (error) {
        console.error('[emailNotifications]: deleteNotification', error);
        commit('sbError', getApiErrorMessage(error, EMAIL_NOTIFICATION.MESSAGES.FAILED_DELETE), { root: true });
        throw error;
      } finally {
        commit('SET_DELETE_PENDING', { id, pending: false });
      }
    },
  },

  getters: {
    fetchNotificationEnabled(state) {
      return state.isNotificationEnabled;
    },
    fetchAvailableNotifications(state) {
      return state.availableNotifications ?? [];
    },
    fetchLocations(state) {
      return state.locations ?? [];
    },
    fetchAllCreatedNotifications(state) {
      return state.createdNotifications ?? [];
    },
    fetchCategories(state) {
      return state.categories ?? [];
    },
    activeScheduleNotifications(state) {
      return (state.createdNotifications || []).filter((doc) => {
        const kind = kindOf(doc);
        return kind === NOTIFICATION_KIND.SCHEDULE || kind === NOTIFICATION_KIND.TRIGGER;
      });
    },
    templateNotifications(state) {
      return (state.createdNotifications || []).filter((doc) => {
        const feature = resolveNotificationFeature(doc);
        return (
          kindOf({ ...doc, feature }) === NOTIFICATION_KIND.TEMPLATE
          || feature === NOTIFICATION_TYPE_KEYS.DASHBOARD
          || feature === NOTIFICATION_TYPE_KEYS.TASK_LIST_ESCALATION
        );
      });
    },
    escalationTemplates(_state, getters) {
      return getters.templateNotifications.filter(
        (doc) => resolveNotificationFeature(doc) === NOTIFICATION_TYPE_KEYS.TASK_LIST_ESCALATION,
      );
    },
    digestTemplates(_state, getters) {
      return getters.templateNotifications.filter(
        (doc) => resolveNotificationFeature(doc) === NOTIFICATION_TYPE_KEYS.DASHBOARD,
      );
    },
    notificationTypeCards(state) {
      const existing = new Set((state.createdNotifications || []).map((doc) => doc.feature));
      return (state.availableNotifications || [])
        .filter((row) => row.status !== false && NOTIFICATION_FEATURE_KIND[row.name] !== NOTIFICATION_KIND.TEMPLATE)
        .map((row) => {
          const feature = row.name;
          const kind = row.kind || NOTIFICATION_FEATURE_KIND[feature];
          const allowMultiple = row.allowMultiple !== false;
          const disabled = allowMultiple === false && existing.has(feature);
          return {
            feature,
            label: row.labelName || NOTIFICATION_FEATURE_LABEL[feature] || feature,
            description: row.description || '',
            icon: NOTIFICATION_FEATURE_ICON[feature] || 'mdi-bell-outline',
            kind,
            allowMultiple,
            disabled,
            disabledReason: disabled ? NOTIFICATION_TYPE_ALREADY_CONFIGURED : '',
          };
        });
    },
    enabledNotificationCount(_state, getters) {
      return getters.activeScheduleNotifications.filter((doc) => isNotificationEnabled(doc)).length;
    },
    emailActivityLogLevel(state) {
      const level = state.notificationConfig?.ui?.email_activity_log;
      return Object.values(EMAIL_ACTIVITY_LOG_LEVEL).includes(level) ? level : EMAIL_ACTIVITY_LOG_LEVEL.DETAILED;
    },
    isEmailActivityVisible(_state, getters) {
      return getters.emailActivityLogLevel !== EMAIL_ACTIVITY_LOG_LEVEL.NONE;
    },
    activityLogs(state) {
      return state.activityLogs;
    },
    isStatusPending: (state) => (id) => state.pendingStatusIds.includes(String(id)),
    isDeletePending: (state) => (id) => state.pendingDeleteIds.includes(String(id)),
    notificationConfig(state) {
      return state.notificationConfig;
    },
    loading(state) {
      return state.loading;
    },
  },
};
